import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginRequest, meRequest } from '../api/auth';
import { setUnauthorizedHandler, useApi } from '../api/client';

const TOKEN_KEY = 'izm.token';
const USER_KEY = 'izm.user';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const persist = useCallback((nextUser, token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    setUser(nextUser || null);
    // Lets DataProvider know to (re)fetch collections in the same tab.
    // The storage event only fires across tabs, so we need a manual one here.
    window.dispatchEvent(new Event('izm:auth-changed'));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    window.dispatchEvent(new Event('izm:auth-changed'));
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
  }, [logout]);

  // Bootstrap: try cached user; if API is configured, refresh from /api/auth/me
  useEffect(() => {
    let cancelled = false;

    const cached = (() => {
      try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();
    const token = localStorage.getItem(TOKEN_KEY);

    if (cached && token) setUser(cached);

    if (useApi && token) {
      meRequest()
        .then((fresh) => {
          if (cancelled) return;
          localStorage.setItem(USER_KEY, JSON.stringify(fresh));
          setUser(fresh);
        })
        .catch(() => {
          if (cancelled) return;
          logout();
        })
        .finally(() => {
          if (!cancelled) setReady(true);
        });
    } else {
      setReady(true);
    }

    return () => { cancelled = true; };
  }, [logout]);

  const login = useCallback(async ({ username, password }) => {
    if (useApi) {
      const result = await loginRequest({ username, password });
      persist(result.user, result.token);
      return result.user;
    }
    // Mock mode for design-only dev: no backend configured.
    await new Promise((resolve) => setTimeout(resolve, 250));
    if (!username || !password || !username.trim() || !password.trim()) {
      const err = new Error('fill_credentials');
      err.code = 'fill_credentials';
      throw err;
    }
    const mockUser = {
      id: 'local-admin',
      firstName: 'Père',
      lastName: 'Munyankindi',
      username,
      role: 'admin',
      permissions: [
        'members:view','members:create','members:update','members:delete',
        'events:view','events:create','events:update','events:delete',
        'payments:view','payments:create','payments:update','payments:delete',
        'users:view','users:create','users:update','users:delete',
        'roles:view','roles:create','roles:update','roles:delete',
        'reports:view','audit:view',
        'reservations:view','reservations:update','transport:view','transport:manage',
        'documents:view','documents:create','documents:update','documents:delete',
        'payouts:view','payouts:manage','settings:manage',
      ],
    };
    persist(mockUser, 'local-mock-token');
    return mockUser;
  }, [persist]);

  const updateLocalUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      if (!permission) return true;
      const perms = user.permissions || [];
      return perms.includes(permission);
    },
    [user]
  );

  const hasRole = useCallback(
    (role) => {
      if (!user) return false;
      if (!role) return true;
      return String(user.role).toLowerCase() === String(role).toLowerCase();
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      ready,
      apiMode: useApi,
      login,
      logout,
      hasPermission,
      hasRole,
      updateLocalUser,
    }),
    [user, ready, login, logout, hasPermission, hasRole, updateLocalUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
